import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { empresasApi, EmpresaSortField } from '../api/empresas'
import { gruposApi } from '../api/grupos'
import { Empresa, EmpresaCreate } from '../types/api'
import ExportButton from '../components/ExportButton'

// Importando componentes wrapper para manter compatibilidade com MUI onde necessário
// ou substituindo por Tailwind onde possível. 
// Para tabelas complexas, manteremos o componente original envelopado em um Card novo.
import EmpresasTable from '../components/Empresas/EmpresasTable'
import EmpresaForm from '../components/Empresas/EmpresaForm'
import DeleteConfirmDialog from '../components/Empresas/DeleteConfirmDialog'

import AddButton from '../components/AddButton'
import ActionBar from '../components/ActionBar'
import { Box, Paper, Alert, Snackbar } from '@mui/material'

const EmpresasPage: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedEmpresa, setSelectedEmpresa] = useState<Empresa | null>(null)
  const [sortField, setSortField] = useState<EmpresaSortField>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Queries e Mutations mantidos iguais à lógica original...
  const { data: gruposRaw = [] } = useQuery({
    queryKey: ['grupos'],
    queryFn: () => gruposApi.list({ includeInactive: true }),
  })
  const grupos = React.useMemo(() => (Array.isArray(gruposRaw) ? gruposRaw : []), [gruposRaw])

  const {
    data: empresasRaw = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['empresas', { sortField, sortDirection }],
    queryFn: () => empresasApi.list(undefined, { sort_by: sortField, sort_direction: sortDirection }),
  })
  const empresas = React.useMemo(() => (Array.isArray(empresasRaw) ? empresasRaw : []), [empresasRaw])

  const exportColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'nome', label: 'Nome' },
    { key: 'cnpj', label: 'CNPJ' },
    { key: 'banco_dados', label: 'Banco de Dados' },
    { key: 'grupo_id', label: 'Grupo ID' },
    { key: 'status', label: 'Status' },
  ], [])

  const createMutation = useMutation({
    mutationFn: (data: EmpresaCreate) => empresasApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas'] })
      setSnackbar({ open: true, message: t('companies.createSuccess'), severity: 'success' })
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.detail || t('companies.title'), severity: 'error' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EmpresaCreate> }) => empresasApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas'] })
      setSnackbar({ open: true, message: t('companies.updateSuccess'), severity: 'success' })
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.detail || t('companies.title'), severity: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => empresasApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empresas'] })
      setSnackbar({ open: true, message: t('companies.deactivateSuccess', 'Empresa inativada com sucesso'), severity: 'success' })
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.response?.data?.detail || t('companies.title'), severity: 'error' })
    },
  })

  // Handlers
  const handleCreate = () => { setSelectedEmpresa(null); setFormOpen(true) }
  const handleEdit = (empresa: Empresa) => { setSelectedEmpresa(empresa); setFormOpen(true) }
  const handleDelete = (empresa: Empresa) => { setSelectedEmpresa(empresa); setDeleteDialogOpen(true) }
  const handleFormSubmit = async (data: EmpresaCreate) => {
    if (selectedEmpresa) await updateMutation.mutateAsync({ id: selectedEmpresa.id, data })
    else await createMutation.mutateAsync(data)
  }
  const handleDeleteConfirm = async () => { if (selectedEmpresa) await deleteMutation.mutateAsync(selectedEmpresa.id) }
  const handleSort = (field: EmpresaSortField) => {
    if (sortField !== field) { setSortField(field); setSortDirection('asc'); return }
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
  }
  const handleCloseSnackbar = () => { setSnackbar({ ...snackbar, open: false }) }

  if (error) {
    return (
      <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
        <Alert severity="error">Erro: {(error as Error).message}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <ActionBar>
        <ExportButton
          data={Array.isArray(empresas) ? empresas : []}
          columns={exportColumns}
          filename="empresas.xlsx"
          sheetName="Empresas"
        />
        <AddButton onClick={handleCreate} title={t('companies.new')} />
      </ActionBar>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <EmpresasTable
          empresas={Array.isArray(empresas) ? empresas : []}
          grupos={Array.isArray(grupos) ? grupos : []}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={isLoading}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      </Paper>

      <EmpresaForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        empresa={selectedEmpresa}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        empresa={selectedEmpresa}
        loading={deleteMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default EmpresasPage
